import type { ComponentType } from "react";
import { lazy, useEffect, useState } from "react";

type LazyDialog = { load: () => Promise<unknown> };

/** A lazy dialog that `useLazyDialog` can load before its first opening. */
export function lazyDialog<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
	return Object.assign(lazy(load), { load });
}

/**
 * Whether a lazy dialog mounts. Its chunk loads when the browser is idle after the page mounts, so it stays out of the
 * first JS chunk. The dialog then mounts closed, and its first opening runs the open animation like any later one:
 * a dialog that mounts already open shows no animation. An opening before the chunk is in mounts the dialog at once.
 * The dialog stays mounted after its first opening, so its close animation runs.
 */
export function useLazyDialog(dialog: LazyDialog, isOpen: boolean) {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasOpened, setHasOpened] = useState(isOpen);
	if (isOpen && !hasOpened) {
		setHasOpened(true);
	}

	useEffect(() => {
		let isCancelled = false;
		// A failed load needs no handling here: the `vite:preloadError` handler reloads the app.
		const preload = () => dialog.load().then(() => !isCancelled && setIsLoaded(true), () => undefined);
		// Safari has no `requestIdleCallback`.
		if ("requestIdleCallback" in window) {
			const id = window.requestIdleCallback(preload);
			return () => {
				isCancelled = true;
				window.cancelIdleCallback(id);
			};
		}
		const id = setTimeout(preload, 1000);
		return () => {
			isCancelled = true;
			clearTimeout(id);
		};
	}, [dialog]);

	return isLoaded || hasOpened;
}
